import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  Stack,
  SimpleGrid,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Button,
  VStack,
  HStack,
  Icon,
  useColorModeValue,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { 
  FaPhone, 
  FaEnvelope, 
  FaMapMarkerAlt, 
  FaClock,
  FaLinkedin,
  FaTwitter,
  FaFacebook
} from 'react-icons/fa';

const ContactInfo = ({ icon, title, text, subtext }) => {
  return (
    <HStack align="start" spacing={4}>
      <Box
        display={'inline-flex'}
        alignItems={'center'}
        justifyContent={'center'}
        w={12}
        h={12}
        color={'white'}
        bg={'brand.500'}
        rounded={'full'}
        flexShrink={0}
      >
        <Icon as={icon} w={5} h={5} />
      </Box>
      <VStack align="start" spacing={0}>
        <Text fontWeight="bold" fontSize="lg">{title}</Text>
        <Text color={'gray.600'}>{text}</Text>
        {subtext && <Text color={'gray.500'} fontSize="sm">{subtext}</Text>}
      </VStack>
    </HStack>
  );
};

export default function ContactSection() {
  const [formData, setFormData] = React.useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = React.useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Here you would normally send the form data to a server
    console.log('Form submitted:', formData);
    setIsSubmitted(true);
    setTimeout(() => setIsSubmitted(false), 5000);
    // Reset form
    setFormData({
      name: '',
      email: '',
      company: '',
      phone: '',
      message: ''
    });
  };

  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  return (
    <Box id="contact" py={16}>
      <Container maxW={'7xl'}>
        <Stack spacing={4} as={Container} maxW={'3xl'} textAlign={'center'} mb={10}>
          <Heading fontSize={'3xl'}>Get In Touch</Heading>
          <Text color={'gray.600'} fontSize={'xl'}>
            Ready to transform your infrastructure? Let's discuss how we can help bring your vision to life.
          </Text>
        </Stack>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={10}>
          {/* Contact Information */}
          <Box>
            <VStack spacing={6} align="start">
              <Heading fontSize={'2xl'}>Contact Information</Heading>
              <Text color={'gray.600'}>
                We're here to help and answer any question you might have. 
                We look forward to hearing from you.
              </Text>
              
              <VStack spacing={4} align="start" pt={4}>
                <ContactInfo 
                  icon={FaPhone}
                  title="Phone"
                  text="+1 (555) 123-4567"
                  subtext="Monday to Friday, 9am to 6pm EST"
                />
                <ContactInfo 
                  icon={FaEnvelope}
                  title="Email"
                  text="info@meshinfrastructure.com"
                  subtext="We'll respond within 24 hours"
                />
                <ContactInfo 
                  icon={FaMapMarkerAlt}
                  title="Office"
                  text="123 Infrastructure Blvd"
                  subtext="New York, NY 10001, USA"
                />
                <ContactInfo 
                  icon={FaClock}
                  title="Business Hours"
                  text="Monday - Friday"
                  subtext="9:00 AM - 6:00 PM EST"
                />
              </VStack>

              <Box pt={4}>
                <Text fontWeight="bold" mb={2}>Follow Us</Text>
                <HStack spacing={4}>
                  <Icon 
                    as={FaLinkedin} 
                    boxSize={6} 
                    color="brand.500" 
                    cursor="pointer" 
                    _hover={{ color: 'brand.600' }} 
                  />
                  <Icon 
                    as={FaTwitter} 
                    boxSize={6} 
                    color="brand.500" 
                    cursor="pointer" 
                    _hover={{ color: 'brand.600' }} 
                  />
                  <Icon 
                    as={FaFacebook} 
                    boxSize={6} 
                    color="brand.500" 
                    cursor="pointer" 
                    _hover={{ color: 'brand.600' }} 
                  />
                </HStack>
              </Box>
            </VStack>
          </Box>

          {/* Contact Form */}
          <Box>
            <Box
              bg={bg}
              rounded={'lg'}
              p={8}
              border={'1px'}
              borderColor={borderColor}
              boxShadow={'lg'}
            >
              <Heading fontSize={'2xl'} mb={6}>Send Us a Message</Heading>
              
              {isSubmitted && (
                <Alert status="success" mb={4} rounded="md">
                  <AlertIcon />
                  Thank you for contacting us! We'll get back to you soon.
                </Alert>
              )}

              <form onSubmit={handleSubmit}>
                <VStack spacing={4}>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="full">
                    <FormControl isRequired>
                      <FormLabel>Name</FormLabel>
                      <Input 
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Your Name" 
                      />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel>Email</FormLabel>
                      <Input 
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="your.email@example.com" 
                      />
                    </FormControl>
                  </SimpleGrid>
                  
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="full">
                    <FormControl>
                      <FormLabel>Company</FormLabel>
                      <Input 
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder="Your Company" 
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Phone</FormLabel>
                      <Input 
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+1 (555) 000-0000" 
                      />
                    </FormControl>
                  </SimpleGrid>
                  
                  <FormControl isRequired>
                    <FormLabel>Message</FormLabel>
                    <Textarea 
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about your project..."
                      rows={6}
                    />
                  </FormControl>
                  
                  <Button
                    type="submit"
                    colorScheme="brand"
                    bg="brand.500"
                    size="lg"
                    w="full"
                    _hover={{ bg: 'brand.600' }}
                  >
                    Send Message
                  </Button>
                </VStack>
              </form>
            </Box>
          </Box>
        </SimpleGrid>
      </Container>
    </Box>
  );
}